import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { Undo2, History, RotateCcw } from "lucide-react";
import { getScheduleHistory, undoScheduleChange, restoreScheduleVersion } from "../../lib/api";

const fmtWhen = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};

export default function ScheduleHistoryDialog({ open, onOpenChange, scheduleId, onRestored }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [authorFilter, setAuthorFilter] = useState("all");

  const load = useCallback(async () => {
    if (!scheduleId) return;
    setLoading(true);
    try {
      const res = await getScheduleHistory(scheduleId);
      setHistory(res.data.history || []);
    } catch {
      toast.error("Failed to load schedule history");
    } finally {
      setLoading(false);
    }
  }, [scheduleId]);

  useEffect(() => { if (open) { setAuthorFilter("all"); load(); } }, [open, load]);

  const handleUndo = async () => {
    setBusy(true);
    try {
      const res = await undoScheduleChange(scheduleId);
      toast.success(`Undid: ${res.data.undone || "last change"}`);
      await load();
      onRestored?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nothing to undo");
    } finally {
      setBusy(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreTarget) return;
    setBusy(true);
    try {
      await restoreScheduleVersion(scheduleId, restoreTarget.id);
      toast.success("Schedule restored to the selected version");
      setRestoreTarget(null);
      await load();
      onRestored?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to restore");
    } finally {
      setBusy(false);
    }
  };

  const latest = history[0];

  // Distinct authors present in the loaded history (for the filter dropdown).
  const authors = useMemo(() => {
    const map = new Map();
    history.forEach((h) => {
      const id = h.changed_by_id || h.changed_by_name || "unknown";
      if (!map.has(id)) map.set(id, h.changed_by_name || "Unknown");
    });
    return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [history]);

  const filtered = useMemo(() => {
    if (authorFilter === "all") return history;
    return history.filter((h) => (h.changed_by_id || h.changed_by_name || "unknown") === authorFilter);
  }, [history, authorFilter]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg" data-testid="schedule-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5" /> Schedule Change History</DialogTitle>
            <DialogDescription>
              Every grid edit is snapshotted. Undo the last change with one click, or restore an earlier version.
            </DialogDescription>
          </DialogHeader>

          <Button
            onClick={handleUndo}
            disabled={busy || loading || !latest}
            className="w-full bg-[#00305E] hover:bg-[#00305E]/90"
            data-testid="undo-last-change-btn"
          >
            <Undo2 className="h-4 w-4 mr-2" />
            {latest ? `Undo last change — ${latest.description}` : "Nothing to undo"}
          </Button>

          {authors.length > 1 && (
            <div className="flex items-center gap-2 mt-2" data-testid="history-author-filter">
              <span className="text-xs text-muted-foreground whitespace-nowrap">Changed by</span>
              <Select value={authorFilter} onValueChange={setAuthorFilter}>
                <SelectTrigger className="h-8" data-testid="history-author-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All staff</SelectItem>
                  {authors.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="max-h-[50vh] overflow-y-auto space-y-1.5 mt-2" data-testid="schedule-history-list">
            {loading ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Loading history…</p>
            ) : history.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center" data-testid="history-empty">
                No changes recorded yet. Edits you make from now on can be undone here.
              </p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center" data-testid="history-author-empty">
                No changes by this staff member.
              </p>
            ) : (
              filtered.map((h, i) => (
                <div
                  key={h.id}
                  className="flex items-center gap-3 rounded-md border px-3 py-2 bg-white"
                  data-testid={`history-row-${i}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{h.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {fmtWhen(h.snapshot_at)} · {h.changed_by_name}
                      {h.id === latest?.id ? " · most recent" : ""}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => setRestoreTarget(h)}
                    data-testid={`restore-version-${i}`}
                  >
                    <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!restoreTarget} onOpenChange={(o) => !o && setRestoreTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore this version?</AlertDialogTitle>
            <AlertDialogDescription>
              This rewinds the schedule to how it looked before “{restoreTarget?.description}” ({fmtWhen(restoreTarget?.snapshot_at)}).
              Any changes made after that point will be discarded.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={handleRestore} data-testid="confirm-restore-version">
              Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
